import { useLayoutEffect, useRef } from "react";

export const ROUTE_SCROLL_RESET_SELECTORS = [
  ".reports-content",
  ".core-dashboard-content",
  ".analytics-page-content",
  ".omni-analytics-page-content",
  ".cinema-analysis-content",
  "[class*='-page-content']",
].join(", ");

export const resetRouteScrollPosition = () => {
  if (typeof window === "undefined") return;

  // Preserve explicit card deep links through the delayed route resets.
  // If the target mounts later, its component handles the initial focus/scroll.
  try {
    const fragment = window.location.hash.slice(1);
    const target = fragment && document.getElementById(decodeURIComponent(fragment));
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ block: "start", behavior: "auto" });
      return;
    }
  } catch {}

  try {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch {
    window.scrollTo(0, 0);
  }

  try {
    document.documentElement.scrollTop = 0;
    document.documentElement.scrollLeft = 0;
    if (document.body) {
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
    }
  } catch {}

  try {
    const containers = document.querySelectorAll(ROUTE_SCROLL_RESET_SELECTORS);
    containers.forEach((node) => {
      if (!node) return;
      if (typeof node.scrollTo === "function") {
        try {
          node.scrollTo({ top: 0, left: 0, behavior: "auto" });
        } catch {
          node.scrollTop = 0;
          node.scrollLeft = 0;
        }
      } else {
        node.scrollTop = 0;
        node.scrollLeft = 0;
      }
    });
  } catch {}
};

/**
 * Reset route-owned scroll surfaces only when the pathname changes.
 *
 * Analytics controls intentionally synchronize state through the query string.
 * Search-only updates must not be treated as new-page navigation, otherwise
 * filters force the document and its content shell back to the top.
 */
export default function useRouteScrollReset(pathname, search = "") {
  const previousPathnameRef = useRef(null);

  useLayoutEffect(() => {
    const pathnameChanged = previousPathnameRef.current !== pathname;
    previousPathnameRef.current = pathname;
    if (!pathnameChanged) return undefined;

    let rafOne = 0;
    let rafTwo = 0;
    const timeoutId = window.setTimeout(() => {
      resetRouteScrollPosition();
    }, 120);
    const lateTimeoutId = window.setTimeout(() => {
      resetRouteScrollPosition();
    }, 360);

    resetRouteScrollPosition();
    rafOne = window.requestAnimationFrame(() => {
      resetRouteScrollPosition();
      rafTwo = window.requestAnimationFrame(() => {
        resetRouteScrollPosition();
      });
    });

    return () => {
      if (rafOne) window.cancelAnimationFrame(rafOne);
      if (rafTwo) window.cancelAnimationFrame(rafTwo);
      window.clearTimeout(timeoutId);
      window.clearTimeout(lateTimeoutId);
    };
  }, [pathname, search]);
}
